/**
 * CraftingPanel -- Upgrade item quality at the crafting vendor.
 *
 * Select an item from inventory, pay gold to upgrade rarity.
 * Normal->Magic (50g), Magic->Rare (200g), Rare->Unique (500g).
 *
 * @module CraftingPanel
 */

import focusManager from '../../input/FocusManager.js';
import eventBus from '../../core/EventBus.js';
import townSystem from '../../systems/TownSystem.js';
import inventorySystem from '../../systems/InventorySystem.js';
import { RARITY_COLORS, RARITY_NAMES } from '../../systems/LootSystem.js';
import { SLOT_INFO } from '../../systems/InventorySystem.js';

export default class CraftingPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'crafting-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Crafting Workshop');
    this._el.setAttribute('data-scene', 'crafting');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('crafting', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'crafting') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = 'Crafting Workshop';
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Status
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    // Instructions
    const desc = document.createElement('div');
    desc.className = 'town-panel-desc';
    desc.textContent = 'Select an item to upgrade its quality tier.';
    panel.appendChild(desc);

    // Content: show upgradeable items
    const content = document.createElement('div');
    content.className = 'town-panel-content';

    const items = inventorySystem.getItems();
    const upgradeableItems = items.filter(item => {
      const upgrade = townSystem.getUpgradeCost(item);
      return upgrade !== null;
    });

    if (upgradeableItems.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No items can be upgraded.';
      content.appendChild(empty);
    } else {
      const grid = document.createElement('div');
      grid.className = 'town-item-grid';

      for (const item of upgradeableItems) {
        const upgrade = townSystem.getUpgradeCost(item);
        const canAfford = (this._party?.gold || 0) >= upgrade.cost;

        const card = document.createElement('div');
        card.className = `town-item-card${!canAfford ? ' town-item-card--disabled' : ''}`;
        card.setAttribute('data-focusable', 'true');

        const name = document.createElement('div');
        name.className = 'town-item-name';
        name.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
        name.textContent = item.name;
        card.appendChild(name);

        const meta = document.createElement('div');
        meta.className = 'town-item-meta';
        meta.textContent = `${RARITY_NAMES[item.rarity]} -> ${RARITY_NAMES[upgrade.targetRarity]}`;
        meta.style.color = RARITY_COLORS[upgrade.targetRarity];
        card.appendChild(meta);

        const price = document.createElement('div');
        price.className = 'town-item-price';
        price.textContent = `${upgrade.cost}g`;
        card.appendChild(price);

        if (canAfford) {
          card.addEventListener('click', () => {
            const result = townSystem.upgradeItem(this._party, item.instanceId);
            this._statusMsg = result.message;
            this._render();
            focusManager.refreshGroup();
          });
        }

        grid.appendChild(card);
      }

      content.appendChild(grid);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }
}
